/**
 * Software implementations of Flash bitmap filters for the Canvas renderer, applied to a layer's
 * straight RGBA pixels (ImageData) in device pixels.
 *
 * - Blur: Flash blurs are repeated box blurs (`quality` passes of a box `blurX` x `blurY` wide);
 *   implemented as separable fractional-width box passes on premultiplied colour.
 * - Glow / DropShadow: blurred (shifted) alpha, scaled by `strength`, tinted with the filter
 *   colour; outer = under the object, inner = on top of the object clipped to its alpha;
 *   knockout / hideObject supported.
 * - Bevel: highlight/shadow from the difference of the blurred alpha shifted along +/- the angle.
 *   UNVERIFIED approximation (not compared with Flash Player output).
 * - ColorMatrix: exact 4x5 matrix on unpremultiplied colour (what the hue filters of the game —
 *   red water, napalm, PI — use).
 * Filter parameters are in stage pixels and are scaled by the view scale only (Flash does not
 * scale filters with the object's own transform).
 */
import { BevelFilter, BitmapFilter, BlurFilter, ColorMatrixFilter, DropShadowFilter, GlowFilter } from '../filters/filters';

export type FilterSpec =
  | { kind: 'blur'; blurX: number; blurY: number; passes: number }
  | {
      kind: 'glow';
      color: number;
      alpha: number;
      blurX: number;
      blurY: number;
      strength: number;
      passes: number;
      inner: boolean;
      knockout: boolean;
      dx: number;
      dy: number;
      hideObject: boolean;
    }
  | {
      kind: 'bevel';
      highlightColor: number;
      highlightAlpha: number;
      shadowColor: number;
      shadowAlpha: number;
      blurX: number;
      blurY: number;
      strength: number;
      passes: number;
      dx: number;
      dy: number;
      type: 'inner' | 'outer' | 'full';
      knockout: boolean;
    }
  | { kind: 'colorMatrix'; matrix: number[] };

function passes(q: number): number {
  q = q | 0;
  return q <= 0 ? 0 : Math.min(15, q);
}

/** Convert a runtime filter object into a render spec (null = unsupported / no-op). */
export function filterSpec(f: BitmapFilter): FilterSpec | null {
  if (f instanceof ColorMatrixFilter) return { kind: 'colorMatrix', matrix: f.matrix };
  if (f instanceof BlurFilter) {
    const p = passes(f.quality);
    if (!p || (f.blurX <= 1 && f.blurY <= 1)) return null;
    return { kind: 'blur', blurX: Math.max(0, Math.min(255, f.blurX)), blurY: Math.max(0, Math.min(255, f.blurY)), passes: p };
  }
  if (f instanceof GlowFilter) {
    return {
      kind: 'glow',
      color: f.color & 0xffffff,
      alpha: Math.max(0, Math.min(1, f.alpha)),
      blurX: Math.max(0, Math.min(255, f.blurX)),
      blurY: Math.max(0, Math.min(255, f.blurY)),
      strength: Math.max(0, Math.min(255, f.strength)),
      passes: passes(f.quality),
      inner: f.inner,
      knockout: f.knockout,
      dx: 0,
      dy: 0,
      hideObject: false,
    };
  }
  if (f instanceof DropShadowFilter) {
    const rad = (f.angle * Math.PI) / 180;
    return {
      kind: 'glow',
      color: f.color & 0xffffff,
      alpha: Math.max(0, Math.min(1, f.alpha)),
      blurX: Math.max(0, Math.min(255, f.blurX)),
      blurY: Math.max(0, Math.min(255, f.blurY)),
      strength: Math.max(0, Math.min(255, f.strength)),
      passes: passes(f.quality),
      inner: f.inner,
      knockout: f.knockout,
      dx: Math.cos(rad) * f.distance,
      dy: Math.sin(rad) * f.distance,
      hideObject: f.hideObject,
    };
  }
  if (f instanceof BevelFilter) {
    const rad = (f.angle * Math.PI) / 180;
    const type = f.type === 'outer' ? 'outer' : f.type === 'full' ? 'full' : 'inner';
    return {
      kind: 'bevel',
      highlightColor: f.highlightColor & 0xffffff,
      highlightAlpha: Math.max(0, Math.min(1, f.highlightAlpha)),
      shadowColor: f.shadowColor & 0xffffff,
      shadowAlpha: Math.max(0, Math.min(1, f.shadowAlpha)),
      blurX: Math.max(0, Math.min(255, f.blurX)),
      blurY: Math.max(0, Math.min(255, f.blurY)),
      strength: Math.max(0, Math.min(255, f.strength)),
      passes: passes(f.quality),
      dx: Math.cos(rad) * f.distance,
      dy: Math.sin(rad) * f.distance,
      type,
      knockout: f.knockout,
    };
  }
  return null; // RawFilter (gradient glow/bevel, convolution): not rendered
}

/** Padding (device px) a filter needs around the object's bounds. */
export function filterPadding(s: FilterSpec, sx: number, sy: number): { x: number; y: number } {
  if (s.kind === 'colorMatrix') return { x: 0, y: 0 };
  const bx = Math.ceil((s.blurX * sx * s.passes) / 2) + 1;
  const by = Math.ceil((s.blurY * sy * s.passes) / 2) + 1;
  if (s.kind === 'blur') return { x: bx, y: by };
  // (-1e-9: cos/sin round-off such as cos(90deg) = 6e-17 must not add a pixel)
  return { x: bx + Math.max(0, Math.ceil(Math.abs(s.dx * sx) - 1e-9)), y: by + Math.max(0, Math.ceil(Math.abs(s.dy * sy) - 1e-9)) };
}

// ------------------------------------------------------------------ blur primitives

/**
 * One horizontal box pass of fractional width `size` over `ch` interleaved channels
 * (values outside the image are 0). Output written to `dst`.
 */
function boxPassH(src: Float32Array, dst: Float32Array, w: number, h: number, ch: number, size: number): void {
  const half = (size - 1) / 2;
  const r = Math.floor(half);
  const frac = half - r; // weight of the samples at distance r + 1
  const inv = 1 / size;
  for (let y = 0; y < h; y++) {
    const row = y * w * ch;
    for (let c = 0; c < ch; c++) {
      let inner = 0; // sum over [x - r, x + r]
      let outer = 0; // sum over [x - r - 1, x + r + 1]
      for (let i = -r - 1; i <= r + 1; i++) {
        if (i < 0 || i >= w) continue;
        const v = src[row + i * ch + c];
        outer += v;
        if (i >= -r && i <= r) inner += v;
      }
      for (let x = 0; x < w; x++) {
        dst[row + x * ch + c] = (inner + frac * (outer - inner)) * inv;
        // slide windows by one
        const ai = x + r + 1;
        const si = x - r;
        const ao = x + r + 2;
        const so = x - r - 1;
        if (ai < w) inner += src[row + ai * ch + c];
        if (si >= 0) inner -= src[row + si * ch + c];
        if (ao < w) outer += src[row + ao * ch + c];
        if (so >= 0) outer -= src[row + so * ch + c];
      }
    }
  }
}

function boxPassV(src: Float32Array, dst: Float32Array, w: number, h: number, ch: number, size: number): void {
  const half = (size - 1) / 2;
  const r = Math.floor(half);
  const frac = half - r;
  const inv = 1 / size;
  const stride = w * ch;
  for (let x = 0; x < w; x++) {
    for (let c = 0; c < ch; c++) {
      const col = x * ch + c;
      let inner = 0;
      let outer = 0;
      for (let i = -r - 1; i <= r + 1; i++) {
        if (i < 0 || i >= h) continue;
        const v = src[i * stride + col];
        outer += v;
        if (i >= -r && i <= r) inner += v;
      }
      for (let y = 0; y < h; y++) {
        dst[y * stride + col] = (inner + frac * (outer - inner)) * inv;
        const ai = y + r + 1;
        const si = y - r;
        const ao = y + r + 2;
        const so = y - r - 1;
        if (ai < h) inner += src[ai * stride + col];
        if (si >= 0) inner -= src[si * stride + col];
        if (ao < h) outer += src[ao * stride + col];
        if (so >= 0) outer -= src[so * stride + col];
      }
    }
  }
}

/** Blur `buf` (interleaved, `ch` channels) in place with `passes` box passes of bx x by. */
export function boxBlur(buf: Float32Array, w: number, h: number, ch: number, bx: number, by: number, passes: number): void {
  const tmp = new Float32Array(buf.length);
  for (let p = 0; p < passes; p++) {
    if (bx > 1) {
      boxPassH(buf, tmp, w, h, ch, bx);
      buf.set(tmp);
    }
    if (by > 1) {
      boxPassV(buf, tmp, w, h, ch, by);
      buf.set(tmp);
    }
  }
}

// ------------------------------------------------------------------ filters on ImageData

export interface PixelBuffer {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

function applyBlur(img: PixelBuffer, bx: number, by: number, n: number): void {
  const { data, width: w, height: h } = img;
  const buf = new Float32Array(w * h * 4);
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    const k = a / 255;
    buf[i] = data[i] * k;
    buf[i + 1] = data[i + 1] * k;
    buf[i + 2] = data[i + 2] * k;
    buf[i + 3] = a;
  }
  boxBlur(buf, w, h, 4, bx, by, n);
  for (let i = 0; i < data.length; i += 4) {
    const a = buf[i + 3];
    if (a <= 0.5) {
      data[i] = data[i + 1] = data[i + 2] = data[i + 3] = 0;
      continue;
    }
    const k = 255 / a;
    data[i] = buf[i] * k;
    data[i + 1] = buf[i + 1] * k;
    data[i + 2] = buf[i + 2] * k;
    data[i + 3] = a;
  }
}

/** Alpha channel shifted by (dx, dy) device px (nearest), optionally inverted (255 - a). */
function alphaPlane(img: PixelBuffer, dx: number, dy: number, invert: boolean): Float32Array {
  const { data, width: w, height: h } = img;
  const out = new Float32Array(w * h);
  const ix = Math.round(dx);
  const iy = Math.round(dy);
  for (let y = 0; y < h; y++) {
    const sy = y - iy;
    for (let x = 0; x < w; x++) {
      const sx = x - ix;
      const a = sx >= 0 && sy >= 0 && sx < w && sy < h ? data[(sy * w + sx) * 4 + 3] : 0;
      out[y * w + x] = invert ? 255 - a : a;
    }
  }
  return out;
}

function applyGlow(img: PixelBuffer, s: Extract<FilterSpec, { kind: 'glow' }>, sx: number, sy: number): void {
  const { data, width: w, height: h } = img;
  const plane = alphaPlane(img, s.dx * sx, s.dy * sy, s.inner);
  if (s.passes > 0) boxBlur(plane, w, h, 1, s.blurX * sx, s.blurY * sy, s.passes);
  const cr = (s.color >>> 16) & 255;
  const cg = (s.color >>> 8) & 255;
  const cb = s.color & 255;
  for (let p = 0, i = 0; p < plane.length; p++, i += 4) {
    let g = (plane[p] * s.strength) / 255;
    if (g > 1) g = 1;
    g *= s.alpha; // glow coverage 0..1
    const sa = data[i + 3] / 255;
    // premultiplied source
    const r0 = (data[i] * sa) | 0;
    const g0 = (data[i + 1] * sa) | 0;
    const b0 = (data[i + 2] * sa) | 0;
    let or: number;
    let og: number;
    let ob: number;
    let oa: number;
    if (!s.inner) {
      // Outer: glow drawn under the object (only where the object is not opaque).
      const ga = g * (1 - sa);
      if (s.knockout) {
        or = cr * ga;
        og = cg * ga;
        ob = cb * ga;
        oa = ga;
      } else if (s.hideObject) {
        or = cr * g;
        og = cg * g;
        ob = cb * g;
        oa = g;
      } else {
        or = r0 + cr * ga;
        og = g0 + cg * ga;
        ob = b0 + cb * ga;
        oa = sa + ga;
      }
    } else {
      // Inner: glow on top of the object, clipped to its alpha (source-atop).
      const gi = g * sa;
      if (s.knockout) {
        or = cr * gi;
        og = cg * gi;
        ob = cb * gi;
        oa = gi;
      } else if (s.hideObject) {
        or = cr * gi;
        og = cg * gi;
        ob = cb * gi;
        oa = gi;
      } else {
        or = cr * gi + r0 * (1 - g);
        og = cg * gi + g0 * (1 - g);
        ob = cb * gi + b0 * (1 - g);
        oa = sa;
      }
    }
    if (oa <= 0) {
      data[i] = data[i + 1] = data[i + 2] = data[i + 3] = 0;
      continue;
    }
    data[i] = or / oa;
    data[i + 1] = og / oa;
    data[i + 2] = ob / oa;
    data[i + 3] = oa * 255;
  }
}

function applyBevel(img: PixelBuffer, s: Extract<FilterSpec, { kind: 'bevel' }>, sx: number, sy: number): void {
  const { data, width: w, height: h } = img;
  const plane = alphaPlane(img, 0, 0, false);
  if (s.passes > 0) boxBlur(plane, w, h, 1, s.blurX * sx, s.blurY * sy, s.passes);
  const ox = Math.round(s.dx * sx);
  const oy = Math.round(s.dy * sy);
  const at = (x: number, y: number) => (x >= 0 && y >= 0 && x < w && y < h ? plane[y * w + x] : 0);
  const hr = (s.highlightColor >>> 16) & 255;
  const hg = (s.highlightColor >>> 8) & 255;
  const hb = s.highlightColor & 255;
  const shr = (s.shadowColor >>> 16) & 255;
  const shg = (s.shadowColor >>> 8) & 255;
  const shb = s.shadowColor & 255;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const diff = (at(x + ox, y + oy) - at(x - ox, y - oy)) / 255;
      let hl = Math.min(1, Math.max(0, diff * s.strength)) * s.highlightAlpha;
      let sh = Math.min(1, Math.max(0, -diff * s.strength)) * s.shadowAlpha;
      const sa = data[i + 3] / 255;
      if (s.type === 'inner') {
        hl *= sa;
        sh *= sa;
      } else if (s.type === 'outer') {
        hl *= 1 - sa;
        sh *= 1 - sa;
      }
      // Effect colour (premultiplied): highlight and shadow never overlap (sign of diff).
      const ea = hl + sh;
      const er = hr * hl + shr * sh;
      const eg = hg * hl + shg * sh;
      const eb = hb * hl + shb * sh;
      const r0 = data[i] * sa;
      const g0 = data[i + 1] * sa;
      const b0 = data[i + 2] * sa;
      let or: number;
      let og: number;
      let ob: number;
      let oa: number;
      if (s.knockout) {
        or = er;
        og = eg;
        ob = eb;
        oa = ea;
      } else if (s.type === 'outer') {
        // effect under the object
        or = r0 + er * (1 - sa);
        og = g0 + eg * (1 - sa);
        ob = b0 + eb * (1 - sa);
        oa = sa + ea * (1 - sa);
      } else {
        // effect over the object
        or = er + r0 * (1 - ea);
        og = eg + g0 * (1 - ea);
        ob = eb + b0 * (1 - ea);
        oa = ea + sa * (1 - ea);
      }
      if (oa <= 0) {
        data[i] = data[i + 1] = data[i + 2] = data[i + 3] = 0;
        continue;
      }
      data[i] = or / oa;
      data[i + 1] = og / oa;
      data[i + 2] = ob / oa;
      data[i + 3] = oa * 255;
    }
  }
}

/** Exact Flash ColorMatrixFilter on straight RGBA (offsets in 0..255 units). */
export function applyColorMatrix(data: Uint8ClampedArray, m: readonly number[]): void {
  const [m0, m1, m2, m3, m4, m5, m6, m7, m8, m9, m10, m11, m12, m13, m14, m15, m16, m17, m18, m19] = m;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    data[i] = m0 * r + m1 * g + m2 * b + m3 * a + m4;
    data[i + 1] = m5 * r + m6 * g + m7 * b + m8 * a + m9;
    data[i + 2] = m10 * r + m11 * g + m12 * b + m13 * a + m14;
    data[i + 3] = m15 * r + m16 * g + m17 * b + m18 * a + m19;
  }
}

/** Apply one filter in place. `sx`/`sy`: device pixels per stage pixel (view scale). */
export function applyFilterSpec(img: PixelBuffer, s: FilterSpec, sx: number, sy: number): void {
  switch (s.kind) {
    case 'colorMatrix':
      applyColorMatrix(img.data, s.matrix);
      break;
    case 'blur':
      applyBlur(img, s.blurX * sx, s.blurY * sy, s.passes);
      break;
    case 'glow':
      applyGlow(img, s, sx, sy);
      break;
    case 'bevel':
      applyBevel(img, s, sx, sy);
      break;
  }
}
